import { Check, Search, ListChecks } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'

import { FormModal } from '@components/branded-modal'
import { AdasBox, AdasTabs, AdasTypography } from '@components/wrapper-components'
import { currencyFormatter } from '../../utils/utils'
import { getProductClass } from '../../utils/productClass'

const getMethodSuffix = (item) => {
  const method = item?.calibration_method || item?.calibrationMethod || item?.method || item?.calType
  return method ? ` (${method})` : ''
}

const getSubtitle = (item) => item?.system || item?.category_name || item?.category?.name || item?.description || ''

const CLASS_TABS = [
  { label: 'Store', value: 'STORE' },
  { label: 'Auto Quote', value: 'AUTO_QUOTE' }
]

export const Drawers = ({ isOpen, onClose, title, data, enableProductClassTabs = false }) => {
  const [itemList, setItemList] = useState([])
  const [displayedProducts, setDisplayedProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [activeClassTab, setActiveClassTab] = useState('STORE')

  useEffect(() => {
    if (data) {
      setItemList(data)
      setDisplayedProducts(
        Object.keys(data)
          .filter((key) => data[key].is_active || (!data[key].is_active && data[key].checked))
          .sort((key1, key2) => data[key1].name.localeCompare(data[key2].name))
      )
    }
  }, [data])

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
      setActiveClassTab('STORE')
    }
  }, [isOpen])

  const handleToggle = (key, nextChecked) => {
    setItemList((prev) => ({
      ...prev,
      [key]: { ...prev[key], checked: nextChecked }
    }))
  }

  // Auto-save on close: parent's onClose receives the latest checked list
  // (regardless of how the modal was dismissed — backdrop tap, ESC, X button).
  // Without data it passes `undefined`, never `[]`: an empty list would tell the parent to remove every line.
  // Hidden keys (other class tab, inactive) keep their checked state, so they are returned too.
  const closeDrawers = () => {
    if (!data) {
      onClose(undefined)
      return
    }
    const list = Object.keys(data)
      .map((k) => itemList[k] ?? data[k])
      .filter((item) => item?.checked)
    onClose(list)
  }

  // When tabs are enabled, scope the list to the active class (Store / Auto Quote).
  // When they're disabled (manually-created QWIs, national agreements, parts/labour),
  // Auto Quote products are not selectable at all — show Store products only so auto-quote
  // pricing never leaks into a list that has no Auto Quote tab. Parts/labour and national
  // lines are STORE-class, so they're unaffected.
  const tabKeys = useMemo(() => {
    if (!enableProductClassTabs) return displayedProducts.filter((key) => getProductClass(itemList[key]) === 'STORE')
    return displayedProducts.filter((key) => getProductClass(itemList[key]) === activeClassTab)
  }, [displayedProducts, itemList, enableProductClassTabs, activeClassTab])

  const filteredProducts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return tabKeys
    return tabKeys.filter((key) => {
      const item = itemList[key]
      const haystack = `${item?.name || ''} ${getSubtitle(item)}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [tabKeys, itemList, searchTerm])

  const totalCount = tabKeys.length
  const selectedCount = useMemo(() => tabKeys.reduce((count, key) => (itemList[key]?.checked ? count + 1 : count), 0), [tabKeys, itemList])

  return (
    itemList && (
      <FormModal
        open={isOpen}
        onClose={closeDrawers}
        title={`Manage ${title}`}
        icon={ListChecks}
        eyebrow={`${selectedCount} of ${totalCount} selected`}
        width='max-w-[560px]'
      >
        <AdasBox>
          {/* Store / Auto Quote tabs — only for product types that have auto-quote variants. */}
          {enableProductClassTabs && (
            <AdasBox className='mb-3'>
              <AdasTabs
                tabs={CLASS_TABS}
                value={activeClassTab}
                onChange={(_event, value) => setActiveClassTab(value)}
                aria-label='product class tab set'
              />
            </AdasBox>
          )}

          {/* Search */}
          <AdasBox className='mb-3 flex items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-3 py-2'>
            <Search className='h-4 w-4 text-[#6B7280]' />
            <input
              type='text'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search manage ${title?.toLowerCase() || ''}...`}
              className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
            />
          </AdasBox>

          {/* List */}
          {filteredProducts.length === 0 ? (
            <AdasBox className='flex items-center justify-center py-10'>
              <AdasTypography className='text-[14px] italic text-[#6B7280]'>No items to display</AdasTypography>
            </AdasBox>
          ) : (
            <AdasBox className='flex flex-col gap-1.5'>
              {filteredProducts.map((productName) => {
                const item = itemList[productName]
                const isChecked = Boolean(item?.checked)
                const titleText = `${item?.name || ''}${getMethodSuffix(item)}`
                const subtitle = getSubtitle(item)
                return (
                  <label
                    key={productName}
                    className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                      isChecked ? 'bg-[#E8EFFF]' : 'bg-white hover:bg-[#F8FAFC]'
                    }`}
                  >
                    <AdasBox className='flex min-w-0 items-center gap-3'>
                      <span
                        className={`inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border transition-colors ${
                          isChecked ? 'border-[#0A1628] bg-[#0A1628]' : 'border-[#D1D5D8] bg-white'
                        }`}
                      >
                        {isChecked && <Check className='h-3 w-3 text-white' strokeWidth={3} />}
                      </span>
                      <input
                        type='checkbox'
                        className='hidden'
                        checked={isChecked}
                        onChange={(e) => handleToggle(productName, e.target.checked)}
                      />
                      <AdasBox className='min-w-0'>
                        <AdasTypography className='truncate text-[14px] font-semibold text-[#0A1628]'>{titleText}</AdasTypography>
                        {subtitle && <AdasTypography className='truncate text-[12px] text-[#6B7280]'>{subtitle}</AdasTypography>}
                      </AdasBox>
                    </AdasBox>
                    <span
                      className={`whitespace-nowrap rounded-md px-3 py-1 text-[13px] font-bold ${
                        isChecked ? 'bg-[#0A1628] text-white' : 'bg-[#EBEDF0] text-[#0A1628]'
                      }`}
                    >
                      {currencyFormatter(item?.price)}
                    </span>
                  </label>
                )
              })}
            </AdasBox>
          )}
        </AdasBox>
      </FormModal>
    )
  )
}

Drawers.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  data: PropTypes.object,
  enableProductClassTabs: PropTypes.bool
}
